import { useCallback, useEffect, useRef, useState } from 'react'

import { apiFetch } from '@/lib/api'
import type { Project } from '@/types/domain.types'
import { resolveProjectCoverUrl } from '@/utils/project-cover'

interface UseProjectsOptions {
  disablePolling?: boolean
}

type FetchParams = Record<string, string | number | null | undefined>

type ExtractedProjects = {
  items: unknown[]
  nextCursor: string | null
  hasMore: boolean
  meta: {
    currentPage?: number | null
    nextPage?: number | null
    limit?: number | null
    offset?: number | null
    total?: number | null
  }
}

const DEFAULT_PROJECTS_PAGE_SIZE = 30

function extractProjectsResponse(raw: any): ExtractedProjects {
  if (Array.isArray(raw)) {
    return { items: raw, nextCursor: null, hasMore: false, meta: {} }
  }

  const visited = new Set<object>()
  const queue: any[] = []
  let itemsCandidate: unknown[] | null = null
  let cursorCandidate: string | null = null
  let hasMoreCandidate: boolean | undefined
  let currentPageCandidate: number | null | undefined
  let nextPageCandidate: number | null | undefined
  let limitCandidate: number | null | undefined
  let offsetCandidate: number | null | undefined
  let totalCandidate: number | null | undefined

  if (raw && typeof raw === 'object') {
    queue.push(raw)
  }

  while (queue.length) {
    const node = queue.shift()
    if (!node || typeof node !== 'object' || visited.has(node)) continue
    visited.add(node)

    if (Array.isArray(node)) {
      if (!itemsCandidate) itemsCandidate = node
      continue
    }

    if (Array.isArray(node.items) && !itemsCandidate) itemsCandidate = node.items
    if (Array.isArray(node.results) && !itemsCandidate) itemsCandidate = node.results
    if (Array.isArray(node.data) && !itemsCandidate) itemsCandidate = node.data

    const possibleCursor =
      node.nextCursor ??
      node.next_cursor ??
      node.cursor ??
      node.next ??
      node.after ??
      node.paginationToken ??
      node.pageToken ??
      node.nextPageToken

    if (typeof possibleCursor === 'string' && possibleCursor.trim().length > 0) {
      cursorCandidate = possibleCursor
    } else if (possibleCursor && typeof possibleCursor === 'object') {
      queue.push(possibleCursor)
    }

    const possibleHasMore =
      node.hasMore ??
      node.has_more ??
      node.more ??
      node.hasNext ??
      node.has_next ??
      node.hasNextPage ??
      node.has_next_page

    if (typeof possibleHasMore === 'boolean') {
      hasMoreCandidate = possibleHasMore
    }

    const possiblePage =
      node.currentPage ??
      node.page ??
      node.pageNumber ??
      node.page_number ??
      node.page_index
    if (typeof possiblePage === 'number') {
      currentPageCandidate = possiblePage
    }

    const possibleNextPage = node.nextPage ?? node.next_page
    if (typeof possibleNextPage === 'number') {
      nextPageCandidate = possibleNextPage
    }

    const possibleLimit = node.limit ?? node.pageSize ?? node.page_size ?? node.per_page
    if (typeof possibleLimit === 'number') {
      limitCandidate = possibleLimit
    }

    const possibleOffset = node.offset ?? node.start ?? node.skip
    if (typeof possibleOffset === 'number') {
      offsetCandidate = possibleOffset
    }

    const possibleTotal = node.total ?? node.totalCount ?? node.total_count ?? node.count
    if (typeof possibleTotal === 'number') {
      totalCandidate = possibleTotal
    }

    if (node.pageInfo && typeof node.pageInfo === 'object') queue.push(node.pageInfo)
    if (node.meta && typeof node.meta === 'object') queue.push(node.meta)
    if (node.pagination && typeof node.pagination === 'object') queue.push(node.pagination)
    if (node.data && typeof node.data === 'object' && !Array.isArray(node.data)) {
      queue.push(node.data)
    }

    for (const value of Object.values(node)) {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        queue.push(value)
      }
    }
  }

  const items = Array.isArray(itemsCandidate) ? itemsCandidate : []

  let derivedHasMore = hasMoreCandidate
  if (derivedHasMore === undefined) {
    if (cursorCandidate) {
      derivedHasMore = true
    } else if (typeof nextPageCandidate === 'number') {
      derivedHasMore = true
    } else if (
      typeof currentPageCandidate === 'number' &&
      typeof totalCandidate === 'number' &&
      currentPageCandidate >= 0
    ) {
      const totalPages =
        typeof limitCandidate === 'number' && limitCandidate > 0
          ? Math.ceil(totalCandidate / limitCandidate)
          : null
      if (totalPages && totalPages > 0) {
        derivedHasMore = currentPageCandidate < totalPages
      }
    } else if (
      typeof offsetCandidate === 'number' &&
      typeof limitCandidate === 'number' &&
      typeof totalCandidate === 'number' &&
      limitCandidate > 0
    ) {
      derivedHasMore = offsetCandidate + items.length < totalCandidate
    } else {
      derivedHasMore = false
    }
  }

  return {
    items,
    nextCursor: cursorCandidate ?? null,
    hasMore: Boolean(derivedHasMore),
    meta: {
      currentPage: currentPageCandidate ?? null,
      nextPage: nextPageCandidate ?? null,
      limit: limitCandidate ?? null,
      offset: offsetCandidate ?? null,
      total: totalCandidate ?? null,
    },
  }
}

function normalizeProject(row: any): Project {
  return {
    id: row.projectId || row.id,
    name: row.name || 'Untitled Project',
    status:
      row?.lastRender?.status === 'ready'
        ? 'complete'
        : row?.lastRender?.status === 'processing'
        ? 'rendering'
        : 'draft',
    duration: row.duration || row.durationFormatted || '0:00',
    createdAt: row.updatedAt || row.createdAt || '',
    updatedAt: row.updatedAt || row.createdAt || '',
    renderUrl: row?.lastRender?.mp4Url,
    coverUrl: resolveProjectCoverUrl(row) ?? undefined,
  }
}

function mergeProjects(existing: Project[], incoming: Project[], mode: 'replace' | 'append'): Project[] {
  if (mode === 'append') {
    const seen = new Set(existing.map((p) => p.id))
    const deduped = incoming.filter((item) => {
      if (!item.id || seen.has(item.id)) return false
      seen.add(item.id)
      return true
    })
    if (deduped.length === 0) {
      return existing
    }
    return [...existing, ...deduped]
  }

  const incomingIds = new Set(incoming.map((p) => p.id))
  const preserved = existing.filter((item) => !incomingIds.has(item.id))
  return [...incoming, ...preserved]
}

export function useProjects(options: UseProjectsOptions = {}) {
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const isInitialLoadRef = useRef(true)
  const hasMoreRef = useRef(false)
  const cursorRef = useRef<string | null>(null)
  const pageInfoRef = useRef<ExtractedProjects['meta']>({})
  const loadingMoreRef = useRef(false)

  const fetchProjects = useCallback(
    async (options: { silent?: boolean; append?: boolean; params?: FetchParams } = {}) => {
      const { silent = false, append = false, params = {} } = options
      const finalParams: FetchParams = { ...params }

      if (append) {
        if (loadingMoreRef.current) return
        loadingMoreRef.current = true
        setLoadingMore(true)
      } else if (!silent) {
        setLoading(true)
      }

      try {
        const searchParams = new URLSearchParams()

        if (!('limit' in finalParams)) {
          finalParams.limit = DEFAULT_PROJECTS_PAGE_SIZE
        }

        if (append && !('cursor' in finalParams)) {
          const cursor = cursorRef.current
          if (cursor) {
            finalParams.cursor = cursor
          }
        }

        for (const [key, value] of Object.entries(finalParams)) {
          if (value === undefined || value === null) continue
          searchParams.set(key, String(value))
        }

        const queryString = searchParams.toString()
        const response = await apiFetch(`/projects${queryString ? `?${queryString}` : ''}`)
        if (!response.ok) {
          throw new Error(`HTTP error ${response.status}`)
        }

        const raw = await response.json()
        const extracted = extractProjectsResponse(raw)
        const normalized = extracted.items.map(normalizeProject)

        cursorRef.current = extracted.nextCursor
        hasMoreRef.current = extracted.hasMore
        setHasMore(extracted.hasMore)
        pageInfoRef.current = extracted.meta

        setProjects((prev) => mergeProjects(prev, normalized, append ? 'append' : 'replace'))
        setError(null)

        if (isInitialLoadRef.current) {
          isInitialLoadRef.current = false
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to fetch projects')
        setError(error)
      } finally {
        if (append) {
          loadingMoreRef.current = false
          setLoadingMore(false)
        } else if (!silent) {
          setLoading(false)
        }
      }
    },
    []
  )

  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current) return
    if (!hasMoreRef.current) return

    const cursor = cursorRef.current
    const pageMeta = pageInfoRef.current

    const params: FetchParams = {}
    if (cursor) {
      params.cursor = cursor
    } else if (pageMeta?.nextPage != null) {
      params.page = pageMeta.nextPage
      params.limit = pageMeta.limit != null ? pageMeta.limit : DEFAULT_PROJECTS_PAGE_SIZE
    } else if (pageMeta?.limit != null) {
      const nextOffset = (pageMeta.offset ?? 0) + pageMeta.limit
      params.offset = nextOffset
      params.limit = pageMeta.limit
    } else {
      params.limit = DEFAULT_PROJECTS_PAGE_SIZE
      return
    }

    await fetchProjects({ append: true, silent: true, params })
  }, [fetchProjects])

  const refetch = useCallback(async () => {
    cursorRef.current = null
    hasMoreRef.current = false
    pageInfoRef.current = {}
    await fetchProjects({ silent: false, append: false, params: { limit: DEFAULT_PROJECTS_PAGE_SIZE } })
  }, [fetchProjects])

  useEffect(() => {
    fetchProjects({ silent: false, params: { limit: DEFAULT_PROJECTS_PAGE_SIZE } })
  }, [fetchProjects])

  useEffect(() => {
    if (options.disablePolling || projects.length === 0) return

    // Adaptive polling: 5s if any project is rendering, 30s otherwise
    const hasActiveRender = projects.some(p => p.status === 'rendering')
    const pollInterval = hasActiveRender ? 5000 : 30000

    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchProjects({ silent: true })
      }
    }, pollInterval)

    return () => clearInterval(timer)
  }, [fetchProjects, options.disablePolling, projects])

  return {
    projects,
    loading,
    loadingMore,
    hasMore,
    error,
    refetch,
    loadMore,
    setProjects,
  }
}
